import { NgComponentOutlet, NgIf } from "@angular/common";
import { Component, inject, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute } from '@angular/router';
import { Subscription } from "rxjs";
import { AuthService } from "../../core/auth/auth.service";
import { PageType } from "../../core/auth/auth.types";
import { ErrorService } from "../../core/services/error.service";
import { PageConfigurationService } from '../../core/services/page-configuration.service';
import { PageConfigurationResponse } from '../../core/types/page-configuration.types';
import { AddNewCreditCardComponent } from "./pages/add-new-credit-card/add-new-credit-card.component";
import { BookOrderComponent } from "./pages/book-order/book-order.component";
import { BookQuoteComponent } from "./pages/book-quote/book-quote.component";
import { ChangeOrderComponent } from "./pages/change-order/change-order.component";
import { DispatchSheetComponent } from "./pages/dispatch-sheet/dispatch-sheet.component";
import { LoginComponent } from "./pages/login/login.component";
import { BolUploadComponent } from "./pages/bol-upload/bol-upload.component";

@Component({
    selector: "app-shortlink-container",
    standalone: true,
    imports: [
        NgIf,
        NgComponentOutlet,
    ],
    templateUrl: "./shortlink-container.component.html",
    styleUrl: "./shortlink-container.component.scss",
})
export class ShortlinkContainerComponent implements OnInit, OnDestroy {
    currentComponent: any = null;
    validationError: string | null = null;

    private _authService = inject(AuthService);
    private _configurationService = inject(PageConfigurationService);
    private _activatedRoute = inject(ActivatedRoute);
    private _subscriptions = new Subscription();
    private _errorService = inject(ErrorService);

    ngOnInit(): void {
        const pageConfigSub = this._configurationService.pageConfiguration$.subscribe(
            (response: PageConfigurationResponse | null) => {
                if (!response) {
                    return;
                }

                if (!response.success) {
                    this._errorService.setError(response.errorMessage || 'Invalid or expired link. Please contact your agent for a new link.');
                    this.currentComponent = null;
                }
                else {
                    this._errorService.clearError();
                }
            }
        );
        this._subscriptions.add(pageConfigSub);

        this.determineComponentToDisplay();
    }

    ngOnDestroy(): void {
        this._subscriptions.unsubscribe();
    }

    /**
     * Determines which component to display based on authentication state and PageType
     */
    private determineComponentToDisplay(): void {
        if (!this._authService.isAuthenticated()) {
            this.currentComponent = LoginComponent;
            return;
        }

        const shortCode = this._authService.getShortCode();
        const pageShortCode = this._activatedRoute.snapshot.params["shortLink"];

        if (!shortCode || shortCode !== pageShortCode) {
            this._authService.signOut();
            this.currentComponent = LoginComponent;
            return;
        }

        const pageType = this._authService.getPageType();

        if (!pageType) {
            this.currentComponent = LoginComponent;
            return;
        }

        this.currentComponent = this.getComponentForPageType(pageType);
    }

    /**
     * Maps PageType enum to the corresponding component
     */
    private getComponentForPageType(pageType: PageType): any {
        switch (pageType) {
            case PageType.AddNewCreditCard:
                return AddNewCreditCardComponent;
            case PageType.BookQuote:
                return BookQuoteComponent;
            case PageType.BookOrder:
                return BookOrderComponent;
            case PageType.ChangeOrder:
                return ChangeOrderComponent;
            case PageType.Dispatch:
                return DispatchSheetComponent;
            case PageType.BillOfLadingUpload:
                return BolUploadComponent;
            default:
                return LoginComponent;
        }
    }

    /**
     * Called when user successfully authenticates
     * This will re-determine which component to display
     */
    onAuthenticationSuccess(): void {
        this.determineComponentToDisplay();
    }
}

